/* global Word */

import type { LayoutConfig, TemplateProfile } from "../../../templates/types";
import type { ExhibitInsertionStrategy } from "../types";
import { TableBuilder } from "../../builders/TableBuilder";
import { ParagraphBuilder } from "../../builders/ParagraphBuilder";
import { setupHeaderCell, setupGraphCell, setupSourceCell } from "../cellSetup";
import { logger } from "../../../core/logging";

/**
 * Inserts a half-width exhibit on the left with a text column on the right.
 * This implementation uses a single flat 3x3 table and OOXML vMerge on the
 * right column so the layout stays stable in Word.
 */
export class HalfWithTextStrategy implements ExhibitInsertionStrategy {
  readonly layoutType = "half-with-text" as const;

  async execute(
    context: Word.RequestContext,
    anchorRange: Word.Range,
    config: LayoutConfig,
    profile: TemplateProfile,
    startingExhibitNumber: number
  ): Promise<Word.Table> {
    const sepWidth = config.sepWidth ?? 11;
    const totalWidth = config.colWidth * 2 + sepWidth;

    logger.debug("[HalfWithTextStrategy] Layout resolved", {
      colWidth: config.colWidth,
      sepWidth,
      totalWidth,
      leftIndent: config.leftIndent ?? 0,
    });

    // ── 1. Insert a single flat 3x3 table ───────────────────────────────────
    const table = anchorRange.insertTable(3, 3, Word.InsertLocation.after);
    TableBuilder.clearAllBorders(table);
    TableBuilder.lockWidth(table, totalWidth);

    // ── 2. Height setup requires one load, then one reload after mutation ────
    table.rows.load("items");
    await context.sync();

    const rowsForHeight = table.rows.items;
    rowsForHeight[0].setHeight(config.row1Height, Word.RowHeightRule.atLeast);
    rowsForHeight[1].setHeight(config.row2Height, Word.RowHeightRule.atLeast);
    rowsForHeight[2].setHeight(config.row3Height, Word.RowHeightRule.atLeast);

    table.rows.load("items");
    await context.sync();

    const rows = table.rows.items;

    // ── 3. Load all cells and pin widths explicitly ──────────────────────────
    rows[0].cells.load("items");
    rows[1].cells.load("items");
    rows[2].cells.load("items");
    await context.sync();

    for (let rowIndex = 0; rowIndex < 3; rowIndex++) {
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[0], config.colWidth);
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[1], sepWidth);
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[2], config.colWidth);
    }

    // ── 4. Separator column stays empty ──────────────────────────────────────
    for (let rowIndex = 0; rowIndex < 3; rowIndex++) {
      TableBuilder.zeroCellPadding(rows[rowIndex].cells.items[1]);
      TableBuilder.clearCellBorders(rows[rowIndex].cells.items[1]);
    }

    // ── 5. Populate the left exhibit column ─────────────────────────────────
    await setupHeaderCell(
      context,
      rows[0].cells.items[0],
      startingExhibitNumber,
      profile,
      { leftIndent: 2 }
    );
    await setupGraphCell(context, rows[1].cells.items[0], { height: config.row2Height, width: config.colWidth });
    await setupSourceCell(context, rows[2].cells.items[0], profile, { leftIndent: 2 });

    // ── 6. Populate the right text column top cell ───────────────────────────
    rows[0].cells.items[2].verticalAlignment = Word.VerticalAlignment.top;
    TableBuilder.zeroCellPadding(rows[0].cells.items[2]);
    rows[0].cells.items[2].body.paragraphs.load("items");
    await context.sync();

    const textParagraphs = rows[0].cells.items[2].body.paragraphs.items;
    if (textParagraphs.length > 0) {
      const textPara = textParagraphs[0];
      textPara.clear();
      ParagraphBuilder.clearSpacing(textPara);
      textPara.alignment = Word.Alignment.left;
      textPara.leftIndent = 2;
      textPara.rightIndent = 0;

      ParagraphBuilder.insertTextRun(textPara, "Graph description", {
        fontFamily: profile.typography.fontFamily,
        fontSize: profile.typography.graphHintSize,
        bold: false,
        color: profile.colorPalette.graphHint,
      });
    }

    // Keep the continuation cells empty and borderless before merging.
    for (let rowIndex = 1; rowIndex < 3; rowIndex++) {
      TableBuilder.zeroCellPadding(rows[rowIndex].cells.items[2]);
      TableBuilder.clearCellBorders(rows[rowIndex].cells.items[2]);
    }

    await context.sync();

    // ── 7. Vertically merge the right column ────────────────────────────────
    table.mergeCells(0, 2, 2, 2);

    return table;
  }
}
